import { setAssessmentRequest, useAppDispatch, useAppRouter, useAssessmentRequest, useCheckoutToken, useValidateFacilityIncomeMutation } from '../../state'
import React, { ChangeEvent, FunctionComponent, useMemo, useRef, useState } from 'react'
import { Button, CARET_PLACEMENTS, Heading, Radio, Text, Tooltip } from '@afterpaytouch/core'
import FormBuilder, { DropdownItems } from '@afterpaytouch/form'
import { useTranslation } from 'next-i18next'
import { IncomeDetailsForm } from './types'
import { IncomeSchemaUpdates, generateDropdownItems, getIncomeSchema, orderedEmploymentStatus, orderedIncomeFrequency } from './utils'
import styles from './style.module.css'
import { EmploymentStatus, FacilityIncomeAssessmentRequest, IncomeFrequency } from '@afterpaytouch/portal-api'
import { Route } from '../../config/router'
import { useMoneyHooks, usePCLDecline } from './hooks'

export const AffordabilityIncome: FunctionComponent = () => {
  const { t } = useTranslation()
  const { push } = useAppRouter()
  const token = useCheckoutToken()
  const [validateIncomeAssessment, { isLoading }] = useValidateFacilityIncomeMutation({ fixedCacheKey: 'income-assessment-cache' })
  const [isFormReady, setIsFormReady] = useState<boolean>(false)
  const [radioValue, setRadioValue] = useState<string>()
  const [showOtherIncomeTooltip, setShowOtherIncomeTooltip] = useState(false)
  const [schemaUpdates, setSchemaUpdates] = useState<IncomeSchemaUpdates>({
    frequency: undefined,
    showOtherIncomeFields: true,
    isValidStatus: false,
  })
  const otherIncomeRef = useRef<HTMLDivElement>(null)
  const testNameSpace = 'affordability-income'
  const { convertToMoney, symbol } = useMoneyHooks()
  const { handleDecline } = usePCLDecline()
  const dispatch = useAppDispatch()
  const assessmentRequest = useAssessmentRequest()
  const id = 'other-income-tooltip'

  const handleSubmit = async (incomeFormData: IncomeDetailsForm): Promise<void> => {
    const { netIncome, otherIncome, employmentStatus, incomeFrequency } = incomeFormData
    const params = {
      token,
      incomeFrequency: incomeFrequency as IncomeFrequency,
      maxFacilityLimit: convertToMoney(assessmentRequest.maxCreditLimit ?? ''),
      netIncome: convertToMoney(netIncome),
      otherIncome: otherIncome ? convertToMoney(otherIncome) : undefined,
      employmentStatus,
      changeInCircumstances: radioValue === 'true',
    } as FacilityIncomeAssessmentRequest

    dispatch(setAssessmentRequest({ employmentStatus, changeInCircumstances: params.changeInCircumstances }))

    try {
      await validateIncomeAssessment(params).unwrap()
      push(Route.AFFORDABILITY_EXPENSES)
    } catch (e) {
      handleDecline()
    }
  }

  const handleChange = (incomeFormData: IncomeDetailsForm): void => {
    const isValidStatus =
      incomeFormData?.employmentStatus === EmploymentStatus.FullTime ||
      incomeFormData?.employmentStatus === EmploymentStatus.PartTime ||
      incomeFormData?.employmentStatus === EmploymentStatus.Casual

    const isEmploymentDefined = incomeFormData?.employmentStatus !== ('' as EmploymentStatus)
    setSchemaUpdates((curr) => ({
      ...curr,
      frequency: incomeFormData?.incomeFrequency,
      showOtherIncomeFields: isValidStatus || !isEmploymentDefined,
      isValidStatus,
    }))
  }

  const handleStateChange = (isValid: boolean): void => {
    setIsFormReady(isValid)
  }

  const handleRadioOption = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value
    setRadioValue(value)
  }

  const handleIconClicked = (): void => {
    setShowOtherIncomeTooltip((current) => !current)
  }

  const otherIncomeTooltipText = (): JSX.Element => <Text>{t('consumerLending:affordability.otherIncome.tooltip')}</Text>

  const { schema } = useMemo(() => {
    const employmentStatusList = generateDropdownItems(t, orderedEmploymentStatus, 'employmentStatus')
    const incomeFrequencyList = generateDropdownItems(t, orderedIncomeFrequency, 'incomeFrequency')
    const options: DropdownItems[] = [
      { key: 'false', label: t('consumerLending:affordability.changeInCircumstances.no') },
      { key: 'true', label: t('consumerLending:affordability.changeInCircumstances.yes') },
    ]
    const components = {
      changeInCircumstancesText: () => {
        return <Text>{t('consumerLending:affordability.changeInCircumstances.text')}</Text>
      },
      changeInCircumstances: () => {
        return (
          <Radio.Group id='income-radio' name='income-radio' testNameSpace={testNameSpace} value={radioValue} onChange={handleRadioOption}>
            <div className={styles.radioWrapper}>
              {options?.map((o) => {
                return (
                  <div className={styles.radioButton} key={o.key}>
                    <Radio.Button name='income-radio' id={`income-radio-${o.key}`} testNameSpace={testNameSpace} value={o.key} kind='Option' label={o.label} />
                  </div>
                )
              })}
            </div>
          </Radio.Group>
        )
      },
      footer: () => {
        return (
          <Button.Primary
            size='Large'
            type='submit'
            disabled={!isFormReady || (radioValue === undefined && schemaUpdates.isValidStatus)}
            padding='Fluid'
            testNameSpace={testNameSpace}
            loading={isLoading}
          >
            {t('consumerLending:affordability.income.cta')}
          </Button.Primary>
        )
      },
    }
    return getIncomeSchema(components, t, employmentStatusList, incomeFrequencyList, schemaUpdates, otherIncomeRef, handleIconClicked, symbol)
  }, [t, isFormReady, schemaUpdates, isLoading, radioValue])

  return (
    <>
      <Heading size={'XXL'}>{t('consumerLending:affordability.incomeHeading')}</Heading>
      <div className={styles.subheading}>
        <Text size={'M'}>{t('consumerLending:affordability.incomeSubheading')}</Text>
      </div>
      <FormBuilder
        schema={schema}
        testNameSpace={testNameSpace}
        onSubmit={handleSubmit}
        onChange={handleChange}
        disabled={false}
        onStateChange={handleStateChange}
      />
      {showOtherIncomeTooltip && (
        <div className={styles.tooltipWrapper}>
          <Tooltip
            id={id}
            triggerRef={otherIncomeRef}
            caretPlacement={CARET_PLACEMENTS.TOP_END}
            testNameSpace={`${testNameSpace}-otherIncome`}
            usePortal={false}
            content={otherIncomeTooltipText()}
            hasCaret={true}
            onClose={() => setShowOtherIncomeTooltip(false)}
            useMobileView={false}
            bubbleSize={{ width: '100%' }}
            skidding={-15}
            closeOnOutsideClick={() => setShowOtherIncomeTooltip(false)}
          />
        </div>
      )}
    </>
  )
}
